import React from "react";

import cx from "classnames";
import { getMinutes, isFuture } from "date-fns";
import moment from "moment";

import type { FellowAttachment } from "~/attachments";
import AppContext, { type AppContextInterface } from "~/common/AppContext";
import type { AttendeesPresenceInfo } from "~/common/Attendees";
import { EditorConfig, ProseReact } from "~/lib/prose";
import { getAllMarks } from "~/lib/prose/marks";
import {
	BulletPoint,
	CustomEmoji,
	Embed,
	HardBreak,
	Header as HeaderNode,
	HorizontalRule,
	KeyResult,
	Mention,
	NumberedListItem,
	Tag,
	TextNode,
	Unstyled,
} from "~/lib/prose/nodes";
import { ImageStrip, ImageStripItem, ImageStripItemCaption, ImageStripItemFile } from "~/lib/prose/nodes/image-strip";
import { Agenda, Collab, ExternalAttribute, Goal, Indent, MultiCursor, Todo } from "~/lib/prose/plugins";
import type { UserPresence } from "~/lib/prose/plugins/multicursor";
import { decodeGlobalId } from "~/lib/relayUtil";
import { getDateString } from "~/lib/utils";
import { AttachmentsPublic } from "~/public/AttachmentsPublic";
import { Avatar } from "~/ui";

import styles from "~/public/css/Note.module.css";
import noteStyles from "~/stream/css/NoteCard.module.css";
import noteHeaderStyles from "~/stream/NoteHeader/NoteHeader.module.css";

export interface FellowParticipant {
	id: string;
	email: string;
	firstName?: string;
	fullName?: string;
	isAnonymous?: boolean;
	image?: string;
}
export interface FellowNote {
	id: string;
	title: string;
	eventStart?: string;
	updatedAt?: string;
	createdAt: string;
	rawDraftJson: {
		doc: { [key: string]: unknown };
	};
	participants: FellowParticipant[];
	attachments: FellowAttachment[];
}

interface NoteProps {
	streamTitleMatchesNoteTitle: boolean;
	note: FellowNote;
	publicLinkId: string;
	inviteId?: string;
	account: { id: string };
}

interface NoteState {
	attendeesPresenceInfo: AttendeesPresenceInfo;
	anonymousParticipants: FellowParticipant[];
	attachments: FellowAttachment[];
}

type PublicEditorConfig = {
	config: EditorConfig;
	collab: Collab;
	multicursor: MultiCursor;
	externalAttribute: ExternalAttribute;
};

export function getPublicEditorConfig(
	currentUserId: string | undefined,
	accountId: string,
	note: FellowNote,
	publicLinkId: string | undefined,
	inviteId: string | undefined,
	isFullView = true,
	onUserPresenceChange: (presence: UserPresence) => void,
	requestAttachment?: (id: string) => Promise<FellowAttachment | undefined>,
): PublicEditorConfig {
	const collab = new Collab({
		documentId: note.id,
		sessionId: window.CLIENT_SESSION_ID,
		publicLinkId,
		inviteId,
		userId: -1,
		initialData: note.rawDraftJson,
	});
	const multicursor = new MultiCursor({
		documentId: note.id,
		sessionId: window.CLIENT_SESSION_ID,
		publicLinkId,
		inviteId,
		user: null,
		collab: collab,
		onUserPresenceChange: onUserPresenceChange,
	});
	const externalAttribute = new ExternalAttribute({
		documentId: note.id,
		sessionId: window.CLIENT_SESSION_ID,
		publicLinkId,
		inviteId,
		initialData: note.rawDraftJson,
	});

	const extensions = [
		...getAllMarks({ disableLinkToolBar: true, linkEditable: false, isPublic: true }),
		new TextNode(),
		new Indent(),
		new Unstyled(),
		new BulletPoint(),
		new NumberedListItem(),
		new HardBreak(),
		new HorizontalRule(),
		new HeaderNode(),
		new ImageStrip({
			contentId: "",
			disableWhenNotEditable: true,
			hidePreview: false,
			requestAttachment: requestAttachment,
		}),
		new ImageStripItem(),
		new ImageStripItemFile(),
		new ImageStripItemCaption(),
		new Goal({ readOnly: true, viewerId: currentUserId, disabledReadOnly: true }),
		new Tag({ showToolbar: false, clickable: false }),
		new Todo({
			note: isFullView ? { id: "-1" } : note,
			readOnly: true,
			isPublic: true,
			viewerId: currentUserId,
			disabledReadOnly: true,
		}),
		new Agenda({
			note: { id: "-1" },
			readOnly: true,
			isPublic: true,
			viewerId: currentUserId,
			disabledReadOnly: true,
		}),
		new Mention({ currentUserId }),
		new Embed({ renderFrame: isFullView }),
		new KeyResult({ disabled: isFullView }),
		new CustomEmoji({ accountId }),
		collab,
		multicursor,
		externalAttribute,
	];

	const config = new EditorConfig({
		extensions: extensions,
		nodes: {
			doc: {
				content: "block+",
			},
		},
	});

	return { config, collab, multicursor, externalAttribute };
}

interface NoteDateProps {
	note: FellowNote;
}

function NoteDate({ note }: NoteDateProps) {
	const { createdAt, eventStart } = note;

	const noteDateTime = React.useMemo(() => {
		const startTime = (eventStart && new Date(eventStart)) || (createdAt ? new Date(createdAt) : null);
		if (!startTime) return null;

		return {
			dateString: getDateString(startTime),
			timeString: startTime.toLocaleTimeString("en", {
				hour: "numeric",
				minute: getMinutes(startTime) === 0 ? undefined : "2-digit",
			}),
			startTime,
		};
	}, [createdAt, eventStart]);

	if (!noteDateTime) return <span>Unscheduled Note</span>;
	const { dateString, timeString, startTime } = noteDateTime;

	return (
		<span
			className={cx(noteHeaderStyles.noteDate, {
				[noteHeaderStyles.isFuture]: isFuture(startTime),
			})}
		>
			<b className={noteHeaderStyles.dateString}>{dateString}</b>
			<span className={cx(noteHeaderStyles.circleSeparator, noteHeaderStyles.isResponsive)} />
			<br className={noteHeaderStyles.noSpaceBreak} />
			<span className={cx(noteHeaderStyles.timeString, noteHeaderStyles.isResponsive)}>{timeString}</span>
		</span>
	);
}

export default class Note extends React.Component<NoteProps, NoteState> {
	static contextType = AppContext;
	declare appContext: React.ContextType<typeof AppContext>;
	state: NoteState = {
		attendeesPresenceInfo: {},
		anonymousParticipants: [],
		attachments: this.props.note.attachments,
	};

	editorConfig: EditorConfig;

	collab: null | Collab = null;
	multicursor: null | MultiCursor = null;
	externalAttribute: null | ExternalAttribute = null;

	intersectionObserver: IntersectionObserver;
	containerRef: React.RefObject<HTMLDivElement> = React.createRef();

	onScreenIntersection = (entries: IntersectionObserverEntry[]) => {
		// just one target at the moment
		if (entries.length !== 1 || !this.multicursor) return;

		const isIntersecting = entries[0]["isIntersecting"];

		if (isIntersecting) {
			this.multicursor.onScreenVisible();
		} else {
			this.multicursor.onScreenHidden();
		}
	};

	constructor(props: NoteProps, appContext: AppContextInterface) {
		super(props);
		const { user } = appContext;
		const data = getPublicEditorConfig(
			user?.id,
			props.account.id,
			props.note,
			props.publicLinkId,
			props.inviteId,
			true,
			this.onUserPresenceChange,
			this.requestAttachment,
		);
		this.editorConfig = data.config;
		this.collab = data.collab;
		this.multicursor = data.multicursor;
		this.externalAttribute = data.externalAttribute;
		this.intersectionObserver = new IntersectionObserver(this.onScreenIntersection);
	}

	requestAttachment = (id: string): Promise<FellowAttachment | undefined> => {
		return new Promise(resolve => {
			let attachment = this.state.attachments.find(x => x.id === id);
			if (attachment) {
				resolve(attachment);
				return;
			}
			const getData = async () => {
				attachment = await this.fetchAttachments(id);
				resolve(attachment);
			};
			getData();
		});
	};

	fetchAttachments = (requestedAttachmentId?: string) => {
		const formData = new FormData();

		formData.append("method", "note_attachments");
		formData.append("note_id", this.props.note.id);

		return fetch(`/public/streams/${this.props.publicLinkId}/`, {
			method: "POST",
			body: formData,
			headers: {
				"x-csrftoken": window.CSRF_TOKEN,
				"x-requested-with": "XMLHttpRequest",
			},
		})
			.then((res: Response) => {
				return res.json();
			})
			.then(({ attachments }: { attachments: FellowAttachment[] }) => {
				this.setState({ attachments });
				if (requestedAttachmentId) {
					return attachments.find(x => x.id === requestedAttachmentId);
				}
				return;
			});
	};

	onUserPresenceChange = (presence: UserPresence) => {
		const { sessionId, user, lastActive, isCurrentlyViewing, platform } = presence;
		const { note } = this.props;
		const { participants } = note;

		this.setState(({ attendeesPresenceInfo, anonymousParticipants }) => {
			const userSessions = attendeesPresenceInfo[user.id];
			const newUserSessions = [
				{
					sessionId,
					isCurrentlyViewing: !!isCurrentlyViewing,
					lastActiveDatetime: lastActive ? moment(lastActive).format() : undefined,
					platform: platform,
				},
				...(userSessions ?? []).filter(s => s.sessionId !== sessionId),
			];
			let newAnonymousParticipants = [...anonymousParticipants];
			if (!participants.some(participant => participant.email === user.email)) {
				const isPresentInAnonymousList = anonymousParticipants.some(
					anonymousParticipant => anonymousParticipant.email === user.email,
				);
				if (!isCurrentlyViewing && isPresentInAnonymousList) {
					newAnonymousParticipants = anonymousParticipants.filter(
						anonymousParticipant => anonymousParticipant.email !== user.email,
					);
				} else if (isCurrentlyViewing && !isPresentInAnonymousList) {
					newAnonymousParticipants = anonymousParticipants.concat([user]);
				}
			}
			return {
				attendeesPresenceInfo: {
					...attendeesPresenceInfo,
					[user.id]: newUserSessions,
				},
				anonymousParticipants: newAnonymousParticipants,
			};
		});
	};

	isAttendeeCurrentlyViewing = (user: FellowParticipant): boolean => {
		// FIXME: If the user is not anonymous and has been removed from the note,
		// they end up in the anonymousParticipants list and have an integer ID
		// instead of a relay ID. For now I just stuck a band-aid on it.
		const userId = user.isAnonymous ? user.id : typeof user.id === "number" ? user.id : decodeGlobalId(user.id)[1];
		const attendeePresenceInfo = this.state.attendeesPresenceInfo[userId];
		return (attendeePresenceInfo ?? []).some(s => s.isCurrentlyViewing);
	};

	compareFn = (a: FellowParticipant, b: FellowParticipant) => {
		const aIsCurrentlyViewing = this.isAttendeeCurrentlyViewing(a);
		const bIsCurrentlyViewing = this.isAttendeeCurrentlyViewing(b);

		if (aIsCurrentlyViewing && !bIsCurrentlyViewing) return 1;
		if (!aIsCurrentlyViewing && bIsCurrentlyViewing) return -1;

		if (!a.isAnonymous && b.isAnonymous) return 1;
		if (a.isAnonymous && !b.isAnonymous) return -1;

		return 0;
	};

	componentDidMount() {
		setTimeout(() => {
			if (this.containerRef.current) this.intersectionObserver.observe(this.containerRef.current);
		}, 1000);
	}

	componentWillUnmount() {
		this.intersectionObserver.disconnect();
	}

	render() {
		const { note, streamTitleMatchesNoteTitle } = this.props;
		const { participants } = note;
		const { anonymousParticipants, attachments } = this.state;

		const sortedParticipants = participants
			.concat(anonymousParticipants)
			.sort(this.compareFn)
			.map(participant => ({
				...participant,
				// Adding a fallback for the fullname
				fullName: participant.fullName || participant.email,
			}));

		return (
			<div ref={this.containerRef} className={noteStyles.noteCard} key={`note-${note.createdAt}`}>
				<article className={noteStyles.noteContent}>
					<header className={cx(noteHeaderStyles.header, noteStyles.header)}>
						<div className={noteHeaderStyles.leftSide}>
							<>
								{!streamTitleMatchesNoteTitle && (
									<span className={noteHeaderStyles.noteTitleContainer}>
										<h2 className={noteHeaderStyles.noteTitle}>
											<span className={noteHeaderStyles.content}>{note.title}</span>
										</h2>
										<div className={noteHeaderStyles.separator} />
									</span>
								)}
								<NoteDate note={note} />
							</>
						</div>
						<div className={cx(noteHeaderStyles.rightSide, styles.attendees)}>
							{sortedParticipants.map(participant => {
								const isCurrentlyViewing = this.isAttendeeCurrentlyViewing(participant);
								return (
									<Avatar
										key={participant.id}
										user={participant}
										outline={isCurrentlyViewing ? "solid" : "none"}
										className={cx(styles.attendee, {
											[styles.isCurrentlyViewing]: isCurrentlyViewing,
										})}
									/>
								);
							})}
						</div>
					</header>
					<div className={noteStyles.editor}>
						<div className={noteStyles.redesignedNoteProseWrapper}>
							<ProseReact
								enableReactHost
								editable={false}
								config={this.editorConfig}
								initialDoc={note.rawDraftJson.doc}
								context={{
									participants,
								}}
							/>
							<AttachmentsPublic
								groupId={note.id}
								attachments={attachments}
								refetchFn={this.fetchAttachments}
								publicLinkId={this.props.publicLinkId}
							/>
						</div>
					</div>
				</article>
			</div>
		);
	}
}
